import { useRef, useCallback } from "react";
import { validateDateRangeLength } from "@util/utils";

/**
 * 管理日期區間欄位（ref、驗證、參數轉換）
 * @param {Object} config
 * @param {number} config.maxRange - 日期限制長度（如 3）
 * @param {'days'|'months'} config.rangeUnit - 單位
 * @returns {{
 *   dateRangeRef: React.RefObject,
 *   validateStartDate: (startDate: any, endDate: any) => void,
 *   validateEndDate: (endDate: any, startDate: any) => void,
 *   getDateParams: () => { startDate: string, endDate: string }
 * }}
 */
const useDateRangeFilter = ({ maxRange, rangeUnit }) => {
	const dateRangeRef = useRef(null);

	const validateStartDate = useCallback(
		(startDate, endDate) => {
			if (!endDate) {
				if (dateRangeRef.current?.setError) {
					dateRangeRef.current.setError(false, "");
				}
				return;
			}
			const result = validateDateRangeLength({
				baseDate: startDate,
				compareDate: endDate,
				maxRange,
				rangeUnit,
				direction: "forward",
			});
			if (dateRangeRef.current?.setError) {
				dateRangeRef.current.setError(result.isInvalid, result.isInvalid ? result.helperText : "");
			}
		},
		// eslint-disable-next-line
		[maxRange, rangeUnit],
	);

	const validateEndDate = useCallback(
		(endDate, startDate) => {
			if (!startDate) {
				if (dateRangeRef.current?.setError) {
					dateRangeRef.current.setError(false, "");
				}
				return;
			}
			const result = validateDateRangeLength({
				baseDate: endDate,
				compareDate: startDate,
				maxRange,
				rangeUnit,
				direction: "backward",
			});
			if (dateRangeRef.current?.setError) {
				dateRangeRef.current.setError(result.isInvalid, result.isInvalid ? result.helperText : "");
			}
		},
		// eslint-disable-next-line
		[maxRange, rangeUnit],
	);

	const getDateParams = useCallback(
		() => {
			const { startDate, endDate } = dateRangeRef.current?.getResult?.() || {};
			return {
				startDate: startDate,
				endDate: endDate,
			};
		},
		// eslint-disable-next-line
		[],
	);

	return {
		dateRangeRef,
		validateStartDate,
		validateEndDate,
		getDateParams,
	};
};

export default useDateRangeFilter;
